import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, AlertTriangle, X } from 'lucide-react';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import {
  activeClientKey,
  markersForClient,
  pruneImportMarkers,
  promoteImportMarker,
  dismissImportMarker,
  evaluateMarkerRow,
  type ImportMarker,
} from '../lib/importNotices';

// Durable completion banner for background (202-queue) imports (2026-10-28).
// Mounted ONCE in Layout after the duplicate mobile/desktop content panes —
// never inside them. Anchored bottom-center, clear of toasts (top-right
// z-[100]), the OCR tray (top-right z-50) and TokenPopup (bottom-right z-50).
// Notices live in localStorage, so they survive reloads, new tabs, and the
// tray's drain `window.location.reload()` — the signal OcrTray's ephemeral
// toast could not reliably deliver.

const DISPLAY_CAP = 3;

function NoticeCard({ marker, onDismiss }: { marker: ImportMarker; onDismiss: (fileId: string) => void }) {
  const notice = marker.notice!;
  const failed = notice.outcome === 'failed' || notice.outcome === 'unclear';
  const Icon = notice.outcome === 'success' ? CheckCircle2 : failed ? AlertCircle : AlertTriangle;
  const iconCls = notice.outcome === 'success' ? 'text-green-600' : failed ? 'text-red-500' : 'text-amber-500';
  const borderCls = notice.outcome === 'success' ? 'border-green-300' : failed ? 'border-red-300' : 'border-amber-300';

  const linkLabel = notice.outcome === 'success' && notice.count > 0
    ? tr('View statements', '查看月結單', '查看月结单')
    : tr('View file', '查看文件', '查看文件');
  const body = notice.outcome === 'success'
    ? (notice.count > 0
        ? tr(
            `${notice.filename} — ${notice.count} bank statements imported`,
            `${notice.filename} — 已匯入 ${notice.count} 份銀行月結單`,
            `${notice.filename} — 已导入 ${notice.count} 份银行月结单`,
          )
        : tr(
            `${notice.filename} finished processing`,
            `${notice.filename} 處理完成`,
            `${notice.filename} 处理完成`,
          ))
    : notice.outcome === 'type_mismatch'
      ? tr(
          `${notice.filename} was not imported — document type mismatch`,
          `${notice.filename} 未匯入 — 文件類型不符`,
          `${notice.filename} 未导入 — 文件类型不符`,
        )
      : tr(
          `${notice.filename} could not be read`,
          `${notice.filename} 無法讀取`,
          `${notice.filename} 无法读取`,
        );

  return (
    <div data-testid={`import-notice-${marker.fileId}`}
      className={`bg-card border ${borderCls} rounded-lg shadow-lg p-3 flex items-start gap-2.5`}>
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${iconCls}`} />
      <div className="flex-1 min-w-0 text-sm">
        <div className="break-words">{body}</div>
        <Link to={notice.linkUrl} className="text-xs font-medium text-blue-600 hover:underline mt-0.5 inline-block">
          {linkLabel} →
        </Link>
      </div>
      <button
        onClick={() => onDismiss(marker.fileId)}
        aria-label={tr('Dismiss', '關閉', '关闭')}
        className="text-muted-foreground hover:text-foreground -mt-0.5 -mr-0.5 p-1 shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function ImportNoticeWatcher() {
  useTranslation(); // subscribe so tr() re-renders on language change
  const [, setTick] = React.useState(0);
  const [clientId, setClientId] = React.useState<string | null>(() => activeClientKey());

  // 2s tick: picks up same-tab writes, client switches, and cross-tab storage
  // events (TokenPopup's storage-polling precedent).
  React.useEffect(() => {
    const id = setInterval(() => { setClientId(activeClientKey()); setTick(t => t + 1); }, 2000);
    const onStorage = () => { setClientId(activeClientKey()); setTick(t => t + 1); };
    window.addEventListener('storage', onStorage);
    return () => { clearInterval(id); window.removeEventListener('storage', onStorage); };
  }, []);

  pruneImportMarkers();
  const markers = markersForClient(clientId);
  const pendingIds = markers.filter(m => m.status === 'pending').map(m => m.fileId);
  const pendingKey = pendingIds.join(',');

  // One list call evaluates every pending marker (rows carry ocr_status +
  // bs_linked_count; never ocr_text). Polls only while something is pending
  // for the ACTIVE client; pauses when the tab is hidden, catches up on focus.
  const { data } = useQuery({
    queryKey: ['import-notice-poll', clientId],
    queryFn: () => api('/file-storage?limit=50'),
    enabled: pendingIds.length > 0,
    refetchInterval: 10_000,
  });

  React.useEffect(() => {
    if (!data) return;
    const rows = ((data as any).data || []) as Array<any>;
    const byId = new Map(rows.map(r => [String(r.id), r]));
    const idList = pendingKey ? pendingKey.split(',') : [];
    const filenameById = new Map(markers.map(m => [m.fileId, m.filename]));
    let changed = false;
    for (const id of idList) {
      const verdict = evaluateMarkerRow(byId.get(id), filenameById.get(id) || '');
      if (verdict.kind === 'promote') { promoteImportMarker(id, verdict.notice); changed = true; }
      else if (verdict.kind === 'expire') { dismissImportMarker(id); changed = true; }
    }
    if (changed) setTick(t => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pendingKey, clientId]);

  // Re-read after promotions/expiries: markers from the tick-time snapshot.
  const notices = markersForClient(clientId)
    .filter(m => m.status === 'noticed' && m.notice)
    .sort((a, b) => (b.notice!.promotedAt || 0) - (a.notice!.promotedAt || 0))
    .slice(0, DISPLAY_CAP);
  if (notices.length === 0) return null;

  return (
    <div data-testid="import-notice-banner"
      className="fixed bottom-4 inset-x-4 z-40 space-y-2 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-md">
      {notices.map(m => (
        <NoticeCard key={m.fileId} marker={m} onDismiss={(id) => { dismissImportMarker(id); setTick(t => t + 1); }} />
      ))}
    </div>
  );
}
